'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import classes from "./other_providers.module.css";
import "./other_providers.css";
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import SafeImage from '@/components/common/safe_image';
import customImageLoader from '../../../../imageLoader';
import CustomSVG from '@/components/common/svg';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import CustomButton from '@/components/common/buttons/button';
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import ChevronLeft from '@/assets/svg/icons/chevron-left.svg';
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import AuthContext from '@/providers/auth-context';
import { getProvidersByFilter } from '@/utils/http';
import { useContext, useEffect, useRef, useState } from 'react';

const OTHER_PROVIDER_CARD_SIZES = '(max-width: 940px) 300px, 400px';
const PROVIDERS_PER_PAGE = 6;

async function triggerSlideHaptic() {
    try {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
        // no-op on web / unsupported platforms
    }
}

export default function OtherProviders({providers}) {
    const [providerItems, setProviderItems] = useState(providers?.providers ?? []);
    const [pager, setPager] = useState(providers?.pager ?? null);
    const loadingRef = useRef(false);
    const totalProviders = pager?.count_rows ?? providerItems.length;
    const hasMultipleProviders = totalProviders > 1;
    const hasMoreProviders = pager && pager.page < pager.page_nums;

    const loadMoreProviders = async () => {
        if (!hasMoreProviders || loadingRef.current) return;

        loadingRef.current = true;
        try {
            const response = await getProvidersByFilter({
                pager: true,
                params: {
                    'filter[is_highlight]': 1,
                    'filter[type_id]': '!1',
                    page: pager.page + 1,
                    per_page: PROVIDERS_PER_PAGE,
                },
            });

            if (response?.success) {
                setProviderItems((currentProviders) => {
                    const currentIds = new Set(currentProviders.map((provider) => provider.id));
                    const nextProviders = (response.response?.providers ?? []).filter((provider) => !currentIds.has(provider.id));
                    return [...currentProviders, ...nextProviders];
                });
                setPager(response.pager);
            }
        } catch (_) {
        } finally {
            loadingRef.current = false;
        }
    };

    return <div className={`other-provider-main ${classes["other-providers-section-main"]}`}>
        <div className="d-flex flex-column align-items-center gap-4 text-center padding-x-8">
            <h2 className={`margin-0 color-gray-800 display-sm-bold ${classes["other-providers-section-main-title"]}`}>Kiemelt szolgáltatóink</h2>
        </div>
        {providerItems.length > 0 && <div className="d-flex flex-column w-100">
            <Swiper
                slidesPerView={"auto"}
                spaceBetween={30}
                freeMode={{
                    enabled: true,
                    sticky: true,
                }}
                pagination={{
                    el: '.other-provider-swiper-custom-pagination',
                    type: 'fraction',
                    renderFraction: (currentClass) =>
                        `<span class="${currentClass}"></span>/<span>${totalProviders}</span>`,
                }}
                navigation={hasMultipleProviders ? {
                    nextEl: '.other-provider-swiper-button-next',
                    prevEl: '.other-provider-swiper-button-prev',
                    disabledClass: 'other-provider-swiper-button-disabled'
                } : false}
                onSlideChange={triggerSlideHaptic}
                modules={[FreeMode, Navigation, Pagination]}
                className={`${classes["other-provider-swiper"]}`}
                slideToClickedSlide={false}
                watchSlidesProgress={true}
                threshold={10}
                noSwipingClass="swiper-no-swiping"
                onReachEnd={loadMoreProviders}
            >
                {providerItems.map(p => <SwiperSlide key={`other-provider-item-${p.id}`} className={`${classes["other-provider-swiper-item"]}`}>
                    <div className={`${classes["other-provider-swiper-item-main-img"]}`}>
                        {p.images && p.images.length > 0
                            ? <img
                                src={customImageLoader({ src: p.images[0], width: 640, quality: 75 })}
                                srcSet={`${customImageLoader({ src: p.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: p.images[0], width: 1080, quality: 75 })} 1080w`}
                                sizes={OTHER_PROVIDER_CARD_SIZES}
                                alt={p.name}
                                loading="lazy"
                                decoding="async"
                                style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                              />
                            : <SafeImage sizes={OTHER_PROVIDER_CARD_SIZES} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                        }
                        {p.type && <div className={`${classes["other-provider-swiper-item-badge"]} text-sm-medium`}>{p.type.name}</div>}
                    </div>
                    <div className={`${classes["other-provider-swiper-item-text-container"]}`}>
                        <div className="d-flex flex-column align-self-stretch text-center">
                            <h3 className={`${classes["provider-card-title"]} margin-0 color-gray-800 text-lg-bold w-100`}>{p.name}</h3>
                            <div className="d-flex flex-row gap-1 align-items-center justify-content-center w-100 text-center">
                                <CustomSVG className={`circle-stroke-gray-700 stroke-gray-700 flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                                <p className="margin-0 color-gray-700 text-sm-regular text-truncate">{(p.address && p.address.city) ? p.address.city : "-"}</p>
                            </div>
                        </div>
                        <div className="swiper-no-swiping"><CustomButton 
                            size="lg" 
                            hierarchy="primary" 
                            href={`/${p.url}`} 
                            rel="noopener noreferrer"
                            target="_blank"
                        >Kapcsolatfelvétel</CustomButton></div>
                        <div className="swiper-no-swiping"><FavoriteCtaButton providerId={p.id} initialActive={Boolean(p.is_favourite)} initialNote={p.favouriteNote || ""} /></div>
                    </div>
                </SwiperSlide>)}
            </Swiper>
            {hasMultipleProviders && <div className={classes["other-provider-swiper-controls"]}>
                <button type="button" className={`other-provider-swiper-button-prev ${classes["other-provider-swiper-nav-button"]}`} aria-label="Előző szolgáltató">
                    <CustomSVG className="stroke-gray-700" SvgFunction={ChevronLeft} height={20} width={20} />
                </button>
                <div className={`other-provider-swiper-custom-pagination ${classes["swiper-custom-pagination"]}`} />
                <button type="button" className={`other-provider-swiper-button-next ${classes["other-provider-swiper-nav-button"]}`} aria-label="Következő szolgáltató">
                    <CustomSVG className="stroke-gray-700" SvgFunction={ChevronRight} height={20} width={20} />
                </button>
            </div>}
        </div>}
        <div><CustomButton size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} href={"/szolgaltatok"} >Szolgáltatói lista</CustomButton></div>
    </div>
}

function FavoriteCtaButton({ providerId, initialActive = false, initialNote = "" }) {
    const authCtx = useContext(AuthContext);
    const normalizedProviderId = Number(providerId);

    useEffect(() => {
        if (!initialActive) return;

        authCtx.setTempFavouriteIds((prev) => (
            prev.some(id => Number(id) === normalizedProviderId) ? prev : [...prev, normalizedProviderId]
        ));

        if (initialNote) {
            authCtx.setTempFavouriteNotes((prev) => (
                prev.find((item) => Number(item.id) === normalizedProviderId)
                    ? prev
                    : [...prev, { id: normalizedProviderId, note: initialNote }]
            ));
        }
    }, [authCtx, normalizedProviderId, initialActive, initialNote]);

    const active = authCtx.tempFavouriteIds.some(id => Number(id) === normalizedProviderId);

    return <CustomButton size="lg" hierarchy="link-gray" onClick={() => authCtx.showFavouriteModal(normalizedProviderId)}>
        {active ? "Eltávolítás a kedvencekből" : "Kedvencekhez adás"}
    </CustomButton>;
}